<template>
  <section class="search">
    <div class="search__input-wrapper">
      <SearchIcon class="search__input-wrapper__search-icon" size="24" />
      <input v-model="searchQuery" placeholder="Search..." />
      <XIcon
        v-show="searchQuery"
        class="search__input-wrapper__x-icon"
        size="16"
        @click="searchQuery = ''"
      />
    </div>
    <div class="search__total-results-wrapper">
      {{ searchLabel }}
    </div>
    <div class="search__filter-tags-wrapper">
      <FilterTags
        :currentRefinements="currentRefinements"
        :filterMap="filterMap"
        @remove-filter="updateFilterAndSearch"
        @clear-filters="clearFilterAndSearch"
      />
    </div>
    <div
      v-show="hits.length"
      :class="[
        'search__filters-wrapper',
        isFiltersModalOpen && 'search__filters-wrapper--modal-open',
      ]"
    >
      <div class="search__filters-wrapper__mobile-header">
        <button
          class="search__filters-wrapper__mobile-header__back-btn btn btn--tertiary btn--default-icon-color"
          @click="isFiltersModalOpen = false"
        >
          <ArrowLeftIcon
            size="25"
            class="search__filters-wrapper__mobile-header__back-btn__icon"
          />
          <span> Filter Results </span>
        </button>
        <button
          class="search__filters-wrapper__mobile-header__clear-btn btn btn--tertiary"
          @click="clearFilterAndSearch"
        >
          <span> Clear filters </span>
        </button>
      </div>
      <ContentFilters
        :filters="facetResults"
        :filterMap="filterMap"
        :currentRefinements="currentRefinedValues"
        :isFreeTrialUser="isFreeTrialUser"
        @update-filters="updateFiltersAndSearch"
      />
    </div>
    <div v-show="hits.length" class="search__sort-wrapper">
      <span class="search__sort-wrapper__label">Sort by:</span>
      <Dropdown
        disabled
        :options="[{ label: 'Newest', facet: 'published_date' }]"
        :selectedOption="{ label: 'Newest', facet: 'published_date' }"
        :handleSelection="updateSortAndSearch"
      ></Dropdown>
    </div>
    <div v-if="hits.length" class="search__results-wrapper">
      <ContentList
        :items="hits"
        :searchIndex="algoliaHelper"
        :isFreeTrialUser="isFreeTrialUser"
        @element-rendered="handleRememberPosition"
      />
    </div>
    <div v-else class="search__no-results-wrapper">
      <img
        class="search__no-results-wrapper__img"
        src="@/assets/images/status/fail-low.png"
        alt="NO RESULTS"
      />
      <h5 class="search__no-results-wrapper__error">
        Oops! We couldn't find any results matching your search.
      </h5>
      <p class="search__no-results-wrapper__instruction">
        Please try searching for something else.
      </p>
    </div>
    <button
      v-show="hits.length"
      :class="[
        'search__open-filters-btn',
        Object.values(currentRefinements).flat().length &&
          'search__open-filters-btn--filtered',
      ]"
      @click="isFiltersModalOpen = true"
    >
      <FilterIcon size="16" />
    </button>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
  cleanSearchStorage,
  getSearchStorage,
  saveCurrentPage,
} from '@/helpers'
import ContentFilters from '@/components/search/filters/ContentFilters.vue'
import ContentList from '@/components/search/ContentList.vue'
import FilterTags from '@/components/shared/FilterTags.vue'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import { SearchIcon, XIcon, FilterIcon, ArrowLeftIcon } from 'vue-feather-icons'
import { algoliaSearchMixin, windowResize } from '@/helpers/mixins'

export default {
  name: 'Search',
  components: {
    ContentFilters,
    SearchIcon,
    ArrowLeftIcon,
    FilterIcon,
    ContentList,
    XIcon,
    FilterTags,
    Dropdown,
  },
  mixins: [algoliaSearchMixin, windowResize],
  metaInfo() {
    return {
      title: `INE - Search`,
      meta: [
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  },
  data() {
    return {
      isFiltersModalOpen: false,
      searchStorage: getSearchStorage(),
      hiddenFiltersOptions: ['is_lab_experience'],
      fixedFiltersOptions: [{ facet: 'is_lab_experience', value: 'false' }],
    }
  },
  computed: {
    ...mapState({
      algoliaKeys: state => state.search.algoliaKeys,
      filterMap: state => state.search.filterMap,
      stateQuery: state => state.search.searchQuery,
      features: state => state.search.features,
      searchFacets: state => state.search.facets,
      skillDive: state => state.search.skillDive,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
      showSkillDiveResults: 'labExperience/showSkillDiveResults',
    }),
    hiddenFilters() {
      return this.showSkillDiveResults ? [] : this.hiddenFiltersOptions
    },
    fixedFilters() {
      return this.showSkillDiveResults ? [] : this.fixedFiltersOptions
    },
    algoliaHelper() {
      return this.algoliaHelpers[this.algoliaKeys.searchIndex]
    },
    totalResults() {
      return this.algoliaHelper?.lastResults?.nbHits || 0
    },
    hits() {
      return this.getIndexHits(this.algoliaKeys.searchIndex)
    },
    searchLabel() {
      const searchQueryLabel = this.searchQuery
        ? ` for “${this.searchQuery}”`
        : ''
      return `${this.totalResults} results ${searchQueryLabel}`
    },
    currentRefinedValues() {
      return this.currentRefinements
    },
    currentRefinements() {
      return this.buildFacetRefinements(
        this.algoliaHelper.state.disjunctiveFacetsRefinements
      )
    },
    facetResults() {
      return this.buildFacetResults(
        this.algoliaHelper?.lastResults?.disjunctiveFacets || []
      )
    },
  },
  watch: {
    showSkillDiveResults: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!oldValue && newValue && this.algoliaHelper) {
          this.fixedFiltersOptions.forEach(({ facet, value }) => {
            this.algoliaHelper.removeDisjunctiveFacetRefinement(facet, value)
            this.setSearchParametersFilter(
              this.algoliaKeys.searchIndex,
              'NOT content_type:collection'
            )
            this.algoliaHelper.search()
          })
        }
      },
    },
  },
  async created() {
    await this.createAlgoliaSearchInstance()
    this.handleAlgoliaLazyLoad(this.algoliaKeys.searchIndex)
    this.handleAlgoliaSearchQuery(this.algoliaKeys.searchIndex, this.stateQuery)
  },
  beforeDestroy() {
    saveCurrentPage(this.page)
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    applyFixedFilters() {
      this.fixedFilters.forEach(({ facet, value }) => {
        this.algoliaHelper.addDisjunctiveFacetRefinement(facet, value)
      })
    },
    handleRememberPosition(elId) {
      if (elId === this.searchStorage.searchTile) {
        setTimeout(() => {
          this.$scrollTo(`#${elId}`, 1000, {
            container: 'body',
            offset: -150,
          })
          cleanSearchStorage()
          this.searchStorage = { ...this.searchStorage, searchTile: '' }
        }, 1000)
      }
    },
    buildFacetRefinements(disjunctiveFacetsRefinements) {
      const refinements = {}
      Object.entries(disjunctiveFacetsRefinements).forEach(
        ([facet, values]) => {
          if (this.hiddenFilters.includes(facet)) return

          if (
            ['has_labs', 'has_cc', 'has_certifications'].includes(facet) &&
            values.length
          ) {
            if (refinements.feature) {
              refinements.feature.push(facet)
            } else {
              refinements.feature = [facet]
            }
          } else if (this.skillDive.includes(facet) && values.length) {
            refinements['skill dive'] = ['is_lab_experience']
          } else {
            refinements[facet] = values
          }
        }
      )

      return refinements
    },
    updateFiltersAndSearch({ facet, value }) {
      const searchIndex = this.algoliaKeys.searchIndex
      if (facet === 'feature') {
        this.toggleAlgoliaFilterAnd(searchIndex, {
          facet: value,
          value: 'true',
        })
      } else if (facet === 'skill dive') {
        this.toggleAlgoliaFilterAnd(searchIndex, {
          facet: value,
          value: 'true',
        })
      } else {
        this.toggleAlgoliaFilterAnd(searchIndex, { facet, value })
      }
    },
    updateSortAndSearch() {
      this.updateSort({ facet: 'published_date' })
    },
    updateFilterAndSearch({ facet, value }) {
      this.updateFiltersAndSearch({ facet, value })
      this.handleAlgoliaSearchQuery(
        this.algoliaKeys.searchIndex,
        this.stateQuery
      )
    },
    clearFilterAndSearch() {
      this.clearAlgoliaRefinements(this.algoliaKeys.searchIndex)
      this.applyFixedFilters()
      this.handleAlgoliaSearchQuery(
        this.algoliaKeys.searchIndex,
        this.stateQuery
      )
    },
    async createAlgoliaSearchInstance() {
      const {
        algoliaKeys: { searchIndex },
        hiddenFilters,
      } = this
      const searchParameters = {
        disjunctiveFacets: this.searchFacets.CONTENT,
        hitsPerPage: 20,
      }

      const options = {
        useUrl: true,
        useDebounceSearchQuery: true,
        hiddenFacets: hiddenFilters,
      }

      if (this.showSkillDiveResults) {
        searchParameters['filters'] = 'NOT content_type:collection'
      }

      await this.initializeAlgoliaSearch(searchIndex, searchParameters, options)
      this.applyFixedFilters()
    },
    buildFacetResults(disjunctiveFacets) {
      const featureFacet = {
        name: 'feature',
        data: {},
      }

      const skillDiveFacet = {
        name: 'skill dive',
        data: {},
        single: true,
        icon: require('@/assets/images/lab-experience/skilldive-logo.png'),
      }

      const featureFacetKeys = ['has_labs', 'has_cc', 'has_certifications']
      const facetTree = disjunctiveFacets.filter(
        df =>
          !featureFacetKeys.includes(df.name) &&
          !this.hiddenFilters.includes(df.name) &&
          !this.skillDive.includes(df.name)
      )

      disjunctiveFacets.forEach(df => {
        if (featureFacetKeys.includes(df.name) && df.data.true) {
          featureFacet.data[df.name] = df.data.true
        }
        if (this.skillDive.includes(df.name) && df.data.true) {
          skillDiveFacet.data[df.name] = df.data.true
        }
      })

      if (Object.keys(featureFacet.data).length) {
        facetTree.splice(1, 0, featureFacet)
      }

      if (
        this.showSkillDiveResults &&
        Object.keys(skillDiveFacet.data).length
      ) {
        facetTree.splice(1, 0, skillDiveFacet)
      }

      return facetTree
    },
  },
}
</script>

<style lang="scss" scoped>
.search {
  background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
  background-size: cover;
  padding: 70px 15px;
  margin: 0 auto;
  display: grid;
  gap: 0 40px;
  @include mobile {
    padding: 80px 20px 25px;
  }
  @include desktop {
    grid-template-columns: 250px auto;
    padding: 125px 100px 25px;
  }
  &__input-wrapper {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0 10px;
    gap: 5px;
    border-radius: 4px;
    background: var(--white-90);

    @include desktop {
      grid-column: 1 / 3;
    }

    &:hover {
      background: var(--white);
    }
    &:focus-within {
      box-shadow: var(--purple-dark-box-shadow);
      background: var(--white);

      svg {
        color: var(--butterfly-bush);
      }
    }
    &__x-icon {
      cursor: pointer;
    }
    svg {
      color: var(--dove-gray);
    }
    input {
      flex: 1;
      background: transparent;
      border: none;
      height: 45px;
      font-family: var(--font-book);
      font-style: normal;
      font-weight: 450;
      font-size: rem(12px);
      line-height: rem(14px);
    }
  }
  &__filters-wrapper {
    display: none;

    @include mobile {
      display: initial;
      margin-top: 20px;
    }

    @include desktop {
      margin-top: 50px;
      grid-column: 1;
      grid-row: 4 / 7;
    }
    &--modal-open {
      display: initial;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 99999;
      background-color: var(--black-50);
      backdrop-filter: blur(35px);
      width: 100%;
      height: 100%;
      padding: 10px;
    }

    &__mobile-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
      height: 40px;
      @include mobile {
        display: none;
      }
    }
  }
  &__open-filters-btn {
    position: fixed;
    bottom: 10px;
    right: 10px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: var(--white);
    width: 40px;
    height: 40px;
    border: none;

    &::before {
      display: none;
      position: absolute;
      content: '';
      top: -2px;
      right: -2px;
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background: var(--primary-ine-gradient);
    }
    &--filtered {
      &::before {
        display: initial;
      }
    }
    @include mobile {
      display: none;
    }
  }
  &__sort-wrapper {
    display: flex;
    flex-flow: column;
    gap: 5px;
    margin-top: 10px;
    @include mobile {
      margin-top: 20px;
      width: 100%;
    }
    @include desktop {
      width: 250px;
      margin-top: 50px;
      grid-row: 4;
      grid-column: 2;
    }

    &__label {
      color: var(--white);
      font-family: var(--font-book);
      font-style: normal;
      font-weight: 400;
      font-size: rem(12px);
      line-height: rem(14px);

      @include mobile {
        display: none;
      }
    }
  }
  &__filter-tags-wrapper {
    display: none;

    @include mobile {
      margin-top: 15px;
      display: initial;
    }

    @include desktop {
      grid-row: 3;
      grid-column: 1 / 3;
    }
  }
  &__results-wrapper {
    margin-top: 20px;
    @include desktop {
      grid-row: 5;
      grid-column: 2;
    }
  }
  &__no-results-wrapper {
    display: flex;
    align-items: center;
    flex-flow: column;
    padding: 20px;
    min-height: 100vh;

    @include desktop {
      grid-row: 4;
      grid-column: 1 / 3;
    }
    &__img {
      width: 280px;
    }
    &__error {
      margin: -30px 0 0 0;
      color: var(--white);
      font-family: var(--font-bold);
      font-style: normal;
      font-weight: 700;
      font-size: rem(20px);
      line-height: rem(26px);
    }
    &__instruction {
      color: var(--light-blue-gray);
      font-family: var(--font-book);
      font-style: normal;
      font-weight: 450;
      font-size: rem(16px);
      line-height: rem(24px);
    }
  }
  &__total-results-wrapper {
    width: 100%;
    color: var(--white);
    font-family: var(--font-medium);
    font-style: normal;
    font-weight: 500;
    font-size: rem(18px);
    line-height: rem(24px);
    margin-top: 10px;

    @include mobile {
      margin-top: 20px;
      font-family: var(--font-bold);
      font-weight: 700;
      font-size: rem(24px);
      line-height: rem(32px);
    }
    @include desktop {
      display: block;
      grid-column: 1 / 3;
    }
  }
}
</style>
